import React, { useState, useEffect } from "react";
import paginationFactory from "react-bootstrap-table2-paginator";
import { useSelector } from "react-redux";
import { SiMicrosoftexcel } from "react-icons/si";
import loadinggif from "../../assets/images/loading.gif";
import BootstrapTable from "react-bootstrap-table-next";
import edMantra from "../../services/edMantra";
import filterFactory, {
  textFilter,
  selectFilter,
  customFilter,
  dateFilter,
} from "react-bootstrap-table2-filter";
// ✅ CSV export utility
const exportToCSV = (data, fileName = "All_Candidates_Report.csv") => {
  if (!data || !data.length) {
    alert("No data available to export.");
    return;
  }

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","), // Header row
    ...data.map(row =>
      headers.map(field => `"${(row[field] ?? "").toString().replace(/"/g, '""')}"`).join(",")
    )
  ];

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CandidateReports = () => {
  const { data } = useSelector((state) => state?.currentSession?.userInfo);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [candidateFilters, setCandidateFilters] = useState({});
  const [totalLeads, setTotalLeads] = useState(0);

  const getAllCandidates = async (
    pg = page,
    limit = sizePerPage,
    filtersToUse = candidateFilters
  ) => {
    try {
      setIsLoading(true);
      const response = await edMantra.getAllCandidates(pg, limit, filtersToUse);
      const { data, totalRecords, errorCode } = response.data;

      if (errorCode === 0 && Array.isArray(data)) {
        setTableData(data);
        setTotalLeads(totalRecords);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const pagination = paginationFactory({
    page,
    sizePerPage,
    totalSize: totalLeads,
    showTotal: true,
    sizePerPageList: [
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "500", value: 500 },
      { text: "1000", value: 1000 },
    ],
    onPageChange: (newPage) => setPage(newPage),
    onSizePerPageChange: (newSize, newPage) => {
      setSizePerPage(newSize);
      setPage(newPage);
    },
  });

  const handleTableChange = (type, { page, sizePerPage, filters }) => {
    setPage(page);
    setSizePerPage(sizePerPage);

    const filterParams = {};
    for (const key in filters) {
      if (filters[key]?.filterVal) {
        filterParams[key] = filters[key].filterVal;
      }
    }

    setCandidateFilters(filterParams);
  };

  useEffect(() => {
    getAllCandidates(page, sizePerPage, candidateFilters);
  }, [page, sizePerPage, candidateFilters]);

  const fetchAllCandidatesForExport = async () => {
    try {
      const response = await edMantra.getAllCandidates(1, 100000, candidateFilters);
      const { data, errorCode } = response.data;
      if (errorCode === 0 && Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching all candidates for export:", error);
      return [];
    }
  };

  const handleRowSelect = (row, isSelected) => {
    if (isSelected) {
      setSelectedRows([...selectedRows, row]);
    } else {
      setSelectedRows(selectedRows.filter((r) => r.candidateId !== row.candidateId));
    }
  };

  const handleRowSelectAll = (isSelected, rows) => {
    setSelectedRows(isSelected ? rows : []);
  };

  const columns = [
    { dataField: "SrNo", text: "Sr No",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "candidateId", text: "Candidate ID",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "firstName", text: "First Name",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "lastName", text: "Last Name",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "gender", text: "Gender",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "maritalStatus", text: "Marital Status",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "email", text: "Email",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "phone", text: "Phone",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "dob", text: "Date of Birth",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "highestQualification", text: "Highest Qualification",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "otherQualification", text: "Other Qualification",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "experience", text: "Experience",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "sector", text: "Industry",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "keySkills", text: "Key Skills",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "language", text: "Language",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "employer", text: "Current Company",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "designation", text: "Designation",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "yearsOfExperience", text: "Years of Experience",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "monthsOfExperience", text: "Months of Experience",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "currentCTC", text: "Current CTC",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "expectedCTC", text: "Expected CTC",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "registeredDate", text: "Registration Date",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "source", text: "Source",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "personName", text: "Source Person Name",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "personContact", text: "Source Person Contact",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "residentialAddress", text: "Residential Address",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "residentialCity", text: "Residential City",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "mailingAddress", text: "Mailing Address",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "mailingCity", text: "Mailing City",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "role", text: "Role",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "aadharDocName", text: "Aadhar Document" },
    { dataField: "agreementDocName", text: "Agreement Document" },
    { dataField: "resumeDocName", text: "Resume Document" },
    { dataField: "latestPhoto", text: "Photo" },
    { dataField: "createdAt", text: "Created At",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
    { dataField: "updatedAt", text: "Updated At",filter: textFilter(), headerStyle: { minWidth: "120px" }, },
  ];

  return (
    <div className="card col-md-12 border-0">
      <button
        className="btn btn-primary btn-sm mb-2 me-2"
        onClick={async () => {
          const allData = await fetchAllCandidatesForExport();
          const validData = allData.filter((item) => item && item.candidateId);

          const trimmedData = validData.map((row) =>
            columns.reduce((acc, col) => {
              acc[col.dataField] = row[col.dataField] ?? "";
              return acc;
            }, {})
          );

          exportToCSV(trimmedData);
        }}
      >
        <SiMicrosoftexcel /> Export All Records
      </button>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center">
          <img src={loadinggif} alt="Loading" />
        </div>
      ) : (
        <div className="table-responsive cus-table">
          <BootstrapTable
            keyField="candidateId"
            data={tableData}
            columns={columns}
            striped
            hover
            condensed
            remote={{ pagination: true }}
            pagination={pagination}
            onTableChange={handleTableChange}
            filter={filterFactory()}
            selectRow={{
              mode: "checkbox",
              clickToSelect: true,
              onSelect: handleRowSelect,
              onSelectAll: handleRowSelectAll,
            }}
            filterPosition="top"
          />
        </div>
      )}
    </div>
  );
};

export default CandidateReports;
